/**
 * How words come on and go off: a pure function of time, so the preview and
 * the export draw the very same frame. The block keeps its full layout the
 * whole time (nothing reflows as letters appear); motion only moves, scales,
 * fades or reveals parts of it.
 */

export type TextEntrance = 'none' | 'fade' | 'rise' | 'pop' | 'typewriter' | 'words';
export type TextExit = 'none' | 'fade' | 'sink' | 'pop';
export const TEXT_ENTRANCES: readonly TextEntrance[] = ['none', 'fade', 'rise', 'pop', 'typewriter', 'words'];
export const TEXT_EXITS: readonly TextExit[] = ['none', 'fade', 'sink', 'pop'];

export interface TextMotion {
  readonly in: TextEntrance;
  readonly out: TextExit;
}

/** Words cut in and out by default: a motion is a choice, not a surprise. */
export const NO_MOTION: TextMotion = { in: 'none', out: 'none' };

/** How long a simple entrance / exit takes; never more than a third of the words' time on screen. */
const ENTER_SEC = 0.45;
const EXIT_SEC = 0.35;
/** Typing pace: per letter, and per word when words appear one by one. */
const PER_LETTER_SEC = 0.05;
const PER_WORD_SEC = 0.22;
/** Typing never takes more than this share of the time on screen, so the words get read whole. */
const MAX_REVEAL_SHARE = 0.5;
/** How far rising / sinking words travel, as a share of the block's height. */
const TRAVEL = 0.35;

/** Letters as people see them (an emoji or an accented letter is one), so typing never splits one. */
const segmenter = typeof Intl !== 'undefined' && 'Segmenter' in Intl ? new Intl.Segmenter(undefined, { granularity: 'grapheme' }) : null;

export function graphemes(text: string): string[] {
  return segmenter ? [...segmenter.segment(text)].map((part) => part.segment) : [...text];
}

/** Words of a line and where each starts in it (characters). */
export const wordsOf = (line: string): { readonly text: string; readonly index: number }[] => [...line.matchAll(/\S+/g)].map((match) => ({ text: match[0], index: match.index ?? 0 }));

/** Part of the block shown while it types itself out: whole letters, or words fading in one after another. */
export interface TextReveal {
  readonly unit: 'letters' | 'words';
  /** How many units show; for words the fraction is how far the next one has faded in. */
  readonly amount: number;
}

/** What to draw at one moment. */
export interface TextMotionFrame {
  readonly opacity: number;
  /** Downward shift as a share of the block's height (negative: up). */
  readonly offsetY: number;
  /** Around the block's centre. */
  readonly scale: number;
  readonly reveal: TextReveal | null;
}

export const STILL_FRAME: TextMotionFrame = { opacity: 1, offsetY: 0, scale: 1, reveal: null };

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const easeOut = (x: number) => 1 - (1 - x) ** 3;
/** Overshoots a little before settling: a pop. */
const easeOutBack = (x: number) => 1 + 2.2 * (x - 1) ** 3 + 1.2 * (x - 1) ** 2;
const easeIn = (x: number) => x * x;

/** Letters and words the block reveals, counted once (for typing speeds). */
function units(text: string): { readonly letters: number; readonly words: number } {
  const lines = text.split('\n');
  return { letters: lines.reduce((sum, line) => sum + graphemes(line).length, 0), words: lines.reduce((sum, line) => sum + wordsOf(line).length, 0) };
}

/** Seconds the entrance takes for these words shown `length` seconds. */
export function entranceLength(motion: TextMotion, text: string, length: number): number {
  if (motion.in === 'none' || !(length > 0)) return 0;
  if (motion.in === 'typewriter') return Math.min(length * MAX_REVEAL_SHARE, Math.max(0.3, units(text).letters * PER_LETTER_SEC));
  if (motion.in === 'words') return Math.min(length * MAX_REVEAL_SHARE, Math.max(0.3, units(text).words * PER_WORD_SEC));
  return Math.min(length / 3, ENTER_SEC);
}

export const exitLength = (motion: TextMotion, length: number): number => (motion.out === 'none' || !(length > 0) ? 0 : Math.min(length / 3, EXIT_SEC));

/**
 * The words at `elapsed` seconds after they came on, out of `length` seconds
 * on screen. Outside that time they aren't drawn at all, so it doesn't matter.
 */
export function textMotionAt(motion: TextMotion, text: string, elapsed: number, length: number): TextMotionFrame {
  if (motion.in === 'none' && motion.out === 'none') return STILL_FRAME;
  let opacity = 1;
  let offsetY = 0;
  let scale = 1;
  let reveal: TextReveal | null = null;

  const enter = entranceLength(motion, text, length);
  if (enter > 0 && elapsed < enter) {
    const linear = clamp01(elapsed / enter);
    const p = easeOut(linear);
    if (motion.in === 'fade') opacity = p;
    else if (motion.in === 'rise') {
      opacity = p;
      offsetY = TRAVEL * (1 - p);
    } else if (motion.in === 'pop') {
      opacity = clamp01(linear * 2.5);
      scale = 0.6 + 0.4 * easeOutBack(linear);
    } else {
      const counts = units(text);
      // Letters land at an even pace, like typing; words each fade in over one step.
      reveal = motion.in === 'typewriter' ? { unit: 'letters', amount: Math.floor(linear * counts.letters + 1e-9) } : { unit: 'words', amount: linear * counts.words };
    }
  }

  const exit = exitLength(motion, length);
  const left = length - elapsed;
  if (exit > 0 && left < exit) {
    const q = easeIn(clamp01(1 - left / exit));
    opacity *= 1 - q;
    if (motion.out === 'sink') offsetY += TRAVEL * q;
    else if (motion.out === 'pop') scale *= 1 - 0.4 * q;
  }
  return { opacity, offsetY, scale, reveal };
}

const pick = <T extends string>(value: unknown, options: readonly T[], fallback: T): T => (options.includes(value as T) ? (value as T) : fallback);

/** A motion read back from storage; words saved with the old fade toggle fade in and out. */
export function parseTextMotion(value: unknown, fade = false): TextMotion {
  if (!value || typeof value !== 'object') return fade ? { in: 'fade', out: 'fade' } : NO_MOTION;
  const raw = value as Partial<Record<keyof TextMotion, unknown>>;
  return { in: pick(raw.in, TEXT_ENTRANCES, 'none'), out: pick(raw.out, TEXT_EXITS, 'none') };
}
